// components/Outreach/OutreachDashboard.js
import React, { useState, useEffect } from "react";
import { getCookie } from "../../services/cookie";
import { CampaignsList } from "./CampaignsList";
import { CreateCampaign } from "./CreateCampaign";
import { IntentionsList } from "./IntentionsList";
import { CreateIntentionsForCampaign } from "./CreateIntentionsForCampaign";
import { WorkerPanel } from "./WorkerPanel";
import { StatsTab } from "./StatsTab";
import { DialogsTab } from "./DialogsTab";
import { ExecutionTab } from "./ExecutionTab";
import { RepliesTab } from "./RepliesTab";
import { ReplyIntentionsTab } from "./ReplyIntentionsTab";

const API_BASE = "https://flautreach.com/flautreach/api";

export function OutreachDashboard({ add }) {
  const token = () => getCookie("authToken");
  const getHeaders = () => {
    const t = token();
    return { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}) };
  };

  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('outreachActiveTab') || 'campaigns';
  });

  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [showCreateIntentions, setShowCreateIntentions] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [stats, setStats] = useState({ campaigns: 0, pending: 0, done: 0, total: 0 });
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    localStorage.setItem('outreachActiveTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    loadStats();
  }, [refreshKey]);

  const loadStats = async () => {
    try {
      // ИСПРАВЛЕНИЕ: Используем пагинацию для подсчета кампаний
      const [campRes, intRes] = await Promise.all([
        fetch(`${API_BASE}/outreach/campaigns/?page=1&page_size=1`, { headers: getHeaders() }),
        fetch(`${API_BASE}/outreach/intentions/?page=1&page_size=1`, { headers: getHeaders() }),
      ]);
      
      // Обработка ответа для кампаний
      let totalCampaigns = 0;
      if (campRes.ok) {
        const campaignsData = await campRes.json();
        // Проверяем разные форматы ответа
        if (Array.isArray(campaignsData)) {
          // Если API возвращает массив, но мы запросили page_size=1
          // Значит API игнорирует пагинацию и возвращает все
          totalCampaigns = campaignsData.length;
          console.warn("API возвращает все кампании, пагинация не работает");
        } else if (campaignsData.pagination?.total !== undefined) {
          // Кастомная пагинация
          totalCampaigns = campaignsData.pagination.total;
        } else if (campaignsData.count !== undefined) {
          // Стандартная DRF пагинация
          totalCampaigns = campaignsData.count;
        } else if (campaignsData.results) {
          // Если есть results, но нет count
          totalCampaigns = campaignsData.results.length;
        } else {
          console.warn("Неизвестный формат ответа кампаний:", campaignsData);
        }
      }

      // Обработка ответа для намерений
      let totalIntentions = 0;
      if (intRes.ok) {
        const intentionsData = await intRes.json();
        if (intentionsData.pagination?.total !== undefined) {
          totalIntentions = intentionsData.pagination.total;
        } else if (intentionsData.count !== undefined) {
          totalIntentions = intentionsData.count;
        }
      }

      // Получаем статистику по статусам
      let pending = 0, done = 0;
      try {
        const [pRes, dRes] = await Promise.all([
          fetch(`${API_BASE}/outreach/intentions/?status=PENDING&page=1&page_size=1`, { headers: getHeaders() }),
          fetch(`${API_BASE}/outreach/intentions/?status=DONE&page=1&page_size=1`, { headers: getHeaders() }),
        ]);
        
        if (pRes.ok) {
          const pData = await pRes.json();
          pending = pData.pagination?.total || pData.count || 0;
        }
        
        if (dRes.ok) {
          const dData = await dRes.json();
          done = dData.pagination?.total || dData.count || 0;
        }
      } catch (err) {
        console.warn("Error loading pending/done stats:", err);
      }

      console.log("Stats loaded:", { totalCampaigns, pending, done, totalIntentions });
      
      setStats({
        campaigns: totalCampaigns,
        pending,
        done,
        total: totalIntentions,
      });
    } catch (err) {
      console.error("Stats error:", err);
    }
  };

  const refresh = () => setRefreshKey(k => k + 1);

  return (
    <div className="outreach-dashboard">
      <div className="rules-header">
        <div>
          <h2>📢 Outreach System</h2>
          <p>Управление аутрич-кампаниями</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn-add" onClick={() => { setShowCreateCampaign(true); setShowCreateIntentions(false); }}>
            + Новая кампания
          </button>
        </div>
      </div>

      {showCreateCampaign ? (
        <CreateCampaign
          onBack={() => { setShowCreateCampaign(false); refresh(); }}
          onCreated={() => { setShowCreateCampaign(false); refresh(); }}
          add={add}
        />
      ) : showCreateIntentions && selectedCampaign ? (
        <CreateIntentionsForCampaign
          campaign={selectedCampaign}
          onBack={() => { setShowCreateIntentions(false); setSelectedCampaign(null); refresh(); }}
          onCreated={() => { setShowCreateIntentions(false); setSelectedCampaign(null); refresh(); }}
          add={add}
        />
      ) : (
        <>
          {/* Статистика */}
          <div className="outreach-stats-grid">
            <div className="outreach-stat-card" onClick={() => setActiveTab("campaigns")} style={{ cursor: "pointer" }}>
              <div className="stat-icon">📢</div>
              <div className="stat-value">{stats.campaigns}</div>
              <div className="stat-label">Кампаний</div>
            </div>
            <div className="outreach-stat-card" onClick={() => setActiveTab("intentions")} style={{ cursor: "pointer" }}>
              <div className="stat-icon">⏳</div>
              <div className="stat-value" style={{ color: "#f59e0b" }}>{stats.pending}</div>
              <div className="stat-label">В очереди</div>
            </div>
            <div className="outreach-stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-value" style={{ color: "#16a34a" }}>{stats.done}</div>
              <div className="stat-label">Выполнено</div>
            </div>
            <div className="outreach-stat-card">
              <div className="stat-icon">📋</div>
              <div className="stat-value">{stats.total}</div>
              <div className="stat-label">Всего</div>
            </div>
          </div>

          {/* Табы */}
          <div className="outreach-tabs">
            <button className={`outreach-tab ${activeTab === "campaigns" ? "active" : ""}`} onClick={() => setActiveTab("campaigns")}>
              📢 Кампании ({stats.campaigns})
            </button>
            <button className={`outreach-tab ${activeTab === "intentions" ? "active" : ""}`} onClick={() => setActiveTab("intentions")}>
              📋 Намерения ({stats.total})
            </button>
            <button className={`outreach-tab ${activeTab === "worker" ? "active" : ""}`} onClick={() => setActiveTab("worker")}>👷 Воркер</button>
            <button className={`outreach-tab ${activeTab === "replies" ? "active" : ""}`} onClick={() => setActiveTab("replies")}>📥Добавить Ответы</button>
            <button className={`outreach-tab ${activeTab === "replyint" ? "active" : ""}`} onClick={() => setActiveTab("replyint")}>📋Реплай Интеншены</button>
            <button className={`outreach-tab ${activeTab === "dialogs" ? "active" : ""}`} onClick={() => setActiveTab("dialogs")}>💬 Диалоги</button>
            <button className={`outreach-tab ${activeTab === "stats" ? "active" : ""}`} onClick={() => setActiveTab("stats")}>📊 Статистика</button>
          </div>

          {activeTab === "campaigns" && (
            <CampaignsList
              onRefresh={refresh}
              onCreateIntentions={(campaign) => {
                setSelectedCampaign(campaign);
                setShowCreateIntentions(true);
              }}
              add={add}
            />
          )}
          {activeTab === "intentions" && (
            <IntentionsList onRefresh={refresh} add={add} />
          )}
          {activeTab === "worker" && <WorkerPanel add={add} />}
          {activeTab === "executions" && <ExecutionTab add={add} />}
          {activeTab === "replies" && <RepliesTab add={add} />}
          {activeTab === "replyint" && <ReplyIntentionsTab add={add} />}
          {activeTab === "dialogs" && <DialogsTab add={add} />}
          {activeTab === "stats" && <StatsTab add={add} />}
        </>
      )}
    </div>
  );
}